import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Handshake, ReceiptText, Ruler, PlugZap, ArrowUpRight, ChevronsUpDown, Check, Search, Sparkles, Wrench, LayoutGrid, Calculator, Scale, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import FissaggiTettoDialog from "@/components/FissaggiTettoDialog";
import ZavorreDialog from "@/components/ZavorreDialog";
import { useAuth } from "@/context/AuthContext";

const TOOLS = [
  { key: "consulenza", title: "Chiavi in mano", desc: "Configura l'impianto chiavi in mano in collaborazione con l'installatore.", Icon: Handshake },
  { key: "bolletta", title: "DEM - Digital Energy Manager", desc: "Verifica le esigenze energetiche del cliente finale caricando la bolletta energetica.", Icon: ReceiptText },
  { key: "tetto", title: "Misura il tetto e crea il layout da satellite", desc: "Scopri quanti pannelli TORRI possono essere installati sul tetto e cura il layout per l'installatore e il cliente finale.", Icon: Ruler },
  { key: "colonnina", title: "Configura la stazione di ricarica", desc: "Scopri il risparmio passando all'elettrico e al fotovoltaico.", Icon: PlugZap },
];

const KINDS = [
  { key: "partner", label: "Partner" },
  { key: "cliente", label: "Clienti" },
  { key: "prospect", label: "Prospect" },
];
const FISS_TOOLS = [
  { key: "inclinato", title: "Fissaggi tetto inclinato", desc: "Calcola staffe, binari, morsetti e giunti dal layout a falde della consulenza tetto.", Icon: Wrench, tipo: "coppo_tegola" },
  { key: "piano", title: "Fissaggi tetto piano", desc: "Calcola presagomate e zavorre dal layout del campo, con abbinamento automatico presagomata/pannello.", Icon: LayoutGrid, tipo: "piano" },
];
const RENDER_CAP = 50;

export default function Tools() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  const [partnersAll, setPartnersAll] = useState([]);
  const [prospectsAll, setProspectsAll] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [kind, setKind] = useState("partner");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [fissOpen, setFissOpen] = useState(false);
  const [zavOpen, setZavOpen] = useState(false);
  const [fissTipo, setFissTipo] = useState("coppo_tegola");

  useEffect(() => {
    (async () => {
      try {
        const [rp, rpr] = await Promise.all([api.get("/partners"), api.get("/prospects")]);
        setPartnersAll((rp.data || []).map((p) => ({ id: p.id, name: p.name, is_partner: !!p.is_partner })));
        setProspectsAll((rpr.data || []).map((p) => ({ id: p.id, name: p.name })));
      } catch { /* noop */ }
      setLoaded(true);
    })();
  }, []);

  const source = useMemo(() => {
    if (kind === "partner") return partnersAll.filter((p) => p.is_partner);
    if (kind === "cliente") return partnersAll.filter((p) => !p.is_partner);
    return prospectsAll;
  }, [kind, partnersAll, prospectsAll]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const arr = s ? source.filter((p) => p.name && p.name.toLowerCase().includes(s)) : source;
    return arr.slice(0, RENDER_CAP);
  }, [source, search]);

  const isAdmin = kind === "admin";
  const kinds = isAdminUser ? [...KINDS, { key: "admin", label: "Admin" }] : KINDS;
  const changeKind = (k) => { setKind(k); setSel(null); setSearch(""); };
  const openTool = (toolKey) => { if (sel) navigate(`/configuratore/${sel.id}?tool=${toolKey}`); };

  return (
    <div className="space-y-6" data-testid="tools-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight">Tools - consulenza all'installatore</h1>
        <p className="text-sm text-muted-foreground">Crea consulenze avanzate con il configuratore torri.solar, per l'installatore (partner, cliente o prospect).</p>
      </div>

      <Card className="p-5" data-testid="tools-installer-card">
        <div className="mb-2 font-mono text-[11px] font-bold uppercase tracking-widest text-primary">Inizia</div>

        <div className="flex flex-wrap items-center gap-8">
        <div className="inline-flex rounded-md border border-white/10 p-0.5" data-testid="tools-kind-tabs">
          {kinds.map((k) => (
            <button key={k.key} onClick={() => changeKind(k.key)} data-testid={`tools-kind-${k.key}`}
              className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${kind === k.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {k.label}
            </button>
          ))}
        </div>

        {isAdmin ? (
          <p className="max-w-md text-xs text-muted-foreground" data-testid="tools-admin-hint">Modalità Admin: tools tecnici attivi su <b>tutte</b> le configurazioni salvate nel configuratore (sola lettura). Apri un tool e carica la configurazione che ti serve.</p>
        ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" className="w-full max-w-md justify-between font-normal" data-testid="tools-installer-combobox">
              <span className="flex items-center gap-2 truncate">
                <Search className="h-4 w-4 shrink-0 opacity-60" />
                {sel ? sel.name : `Cerca ${kind === "prospect" ? "prospect" : kind === "cliente" ? "cliente" : "partner"}…`}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput placeholder="Digita per cercare…" value={search} onValueChange={setSearch} data-testid="tools-installer-search" />
              <CommandList>
                <CommandEmpty>{loaded ? "Nessun risultato" : "Caricamento…"}</CommandEmpty>
                <CommandGroup>
                  {filtered.map((p) => (
                    <CommandItem key={p.id} value={p.id} onSelect={() => { setSel(p); setOpen(false); }} data-testid={`tools-installer-opt-${p.id}`}>
                      <Check className={`mr-2 h-4 w-4 ${sel?.id === p.id ? "opacity-100" : "opacity-0"}`} />
                      <span className="truncate">{p.name}</span>
                    </CommandItem>
                  ))}
                  {!search && source.length > RENDER_CAP && (
                    <div className="px-3 py-2 text-[11px] text-muted-foreground" data-testid="tools-installer-more">
                      Mostrati i primi {RENDER_CAP} di {source.length}. Digita per cercare.
                    </div>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        )}
        </div>
        {sel && <p className="mt-2 text-xs text-muted-foreground" data-testid="tools-installer-selected">Selezionato: <span className="font-semibold text-foreground">{sel.name}</span></p>}
      </Card>

      {!isAdmin && (
      <div>
        <div className="mb-3 font-mono text-[11px] font-bold uppercase tracking-widest text-primary">Tools commerciali <span className="normal-case text-muted-foreground">(collegati a configuratore.torri.solar)</span></div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {TOOLS.map(({ key, title, desc, Icon }) => (
            <button
              key={key}
              type="button"
              disabled={!sel}
              onClick={() => openTool(key)}
              data-testid={`tool-card-${key}`}
              className="group flex flex-col rounded-xl border border-border bg-card p-6 text-left transition-all hover:border-primary/60 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:bg-card"
            >
              <span className="mb-4 flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary transition-colors group-hover:bg-primary/25">
                  <Icon className="h-6 w-6" />
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary" data-testid={`tool-ai-badge-${key}`}>
                  <Sparkles className="h-3 w-3" /> AI driven
                </span>
              </span>
              <span className="font-heading text-lg font-bold tracking-tight">{title}</span>
              <span className="mt-1 text-sm text-muted-foreground">{desc}</span>
              <ArrowUpRight className="mt-4 h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
            </button>
          ))}
        </div>
        {!sel && !isAdmin && <p className="mt-3 text-xs text-muted-foreground">Seleziona prima un installatore per abilitare gli strumenti.</p>}
      </div>
      )}

      <div>
        <div className="mb-3 flex items-center justify-between">
          <div className="font-mono text-[11px] font-bold uppercase tracking-widest text-primary">Tools tecnici <span className="normal-case text-muted-foreground">(dimensionamento strutture di fissaggio)</span></div>
          <Button variant="outline" size="sm" onClick={() => navigate("/parametri-dimensionamento")} data-testid="tools-parametri-link">
            <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Parametri di dimensionamento
          </Button>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {FISS_TOOLS.map(({ key, title, desc, Icon, tipo }) => (
            <button
              key={key}
              type="button"
              disabled={!sel && !isAdmin}
              onClick={() => { setFissTipo(tipo); setFissOpen(true); }}
              data-testid={`fiss-tool-card-${key}`}
              className="group flex flex-col rounded-xl border border-border bg-card p-6 text-left transition-all hover:border-primary/60 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:bg-card"
            >
              <span className="mb-4 flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary transition-colors group-hover:bg-primary/25">
                  <Icon className="h-6 w-6" />
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground" data-testid={`fiss-tool-badge-${key}`}>
                  <Calculator className="h-3 w-3" /> Calcolo
                </span>
              </span>
              <span className="font-heading text-lg font-bold tracking-tight">{title}</span>
              <span className="mt-1 text-sm text-muted-foreground">{desc}</span>
              <ArrowUpRight className="mt-4 h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
            </button>
          ))}
          <button
            type="button"
            disabled={!sel && !isAdmin}
            onClick={() => setZavOpen(true)}
            data-testid="fiss-tool-card-zavorre"
            className="group flex flex-col rounded-xl border border-border bg-card p-6 text-left transition-all hover:border-primary/60 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:bg-card"
          >
            <span className="mb-4 flex items-center justify-between">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary transition-colors group-hover:bg-primary/25">
                <Scale className="h-6 w-6" />
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-600 dark:text-amber-400" data-testid="fiss-tool-badge-zavorre">
                <Scale className="h-3 w-3" /> Pre-dim.
              </span>
            </span>
            <span className="font-heading text-lg font-bold tracking-tight">Pre-dimensionamento zavorre</span>
            <span className="mt-1 text-sm text-muted-foreground">Motore parametrico NTC per il numero minimo di zavorre per PRSG su tetto piano (vento, attrito, zonizzazione, verifiche).</span>
            <ArrowUpRight className="mt-4 h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
          </button>
        </div>
        {!sel && !isAdmin && <p className="mt-3 text-xs text-muted-foreground">Seleziona prima un installatore per abilitare gli strumenti.</p>}
      </div>

      <FissaggiTettoDialog
        open={fissOpen}
        onOpenChange={setFissOpen}
        installerId={sel?.id}
        moduleRow={null}
        tettoConfig={null}
        forceTipo={fissTipo}
        inclinatoOnly={fissTipo !== "piano"}
        pianoOnly={fissTipo === "piano"}
        pdfMode
        installerName={sel?.name}
        installerIsPartner={!!sel?.is_partner}
        adminAll={isAdmin}
        onApply={() => { toast.success("Dimensionamento calcolato. Per applicarlo a un preventivo usa il Preventivatore."); setFissOpen(false); }}
      />
      <ZavorreDialog open={zavOpen} onOpenChange={setZavOpen} installerId={sel?.id} installerName={sel?.name} installerIsPartner={!!sel?.is_partner} adminAll={isAdmin} />
    </div>
  );
}
